// Components
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import NewAccountButton from '../components/NewAccountButton';
import DeleteAccountButton from '../components/DeleteAccountButton';
import SetPasswordAccountButton from '../components/SetPasswordAccountButton';
import SetAccountQuotaButton from '../components/SetAccountQuotaButton';
import ListKeysButton from '../components/ListKeysButton';
import LoadingSpinner from '../components/LoadingSpinner';
import { namespace } from '../../../config.json';

// External
import Reactable from 'reactable';

// To handle requests
import AccountsAPI from '../utils/AccountsAPI';
import getDateFormat from '../utils/getDateFormat';
import MetricsAPI from '../utils/MetricsAPI';

// CSS
import '../components/Panel.css';
import '../pages/ResponsiveTable.css';
import brandingStyles from '../branding/brandingStyles';

// Tools
import copyToClipboard from '../utils/CopyToClipboard';
import compareDates from '../utils/compareDates';
import metricsTools from '../utils/metricsTools';

// Setting reactable values instead of regular table
const Table = Reactable.Table;
const Thead = Reactable.Thead;
const Th = Reactable.Th;
const Tr = Reactable.Tr;
const Td = Reactable.Td;

class AccountsComponent extends Component {

    constructor() {
        super();

        const nbItemsPagination = JSON.parse(
          localStorage.getItem('scality-nbitems-vault-accounts')
        );
        this.state = {
            accounts: [],
            filterText: '',
            currentIndex: 0,
            maxIndex: -1,
            marker: null,
            nbItems: nbItemsPagination || 20,
            dataLoaded: false,
        };
        // We need to bind this to functions so we can have
        // the proper this reference inside the method
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handlePreviousPage = this.handlePreviousPage.bind(this);
        this.handleNextPage = this.handleNextPage.bind(this);
        this.handleUserAction = this.handleUserAction.bind(this);
        this.getAccountRow = this.getAccountRow.bind(this);
        this.refresh = this.refresh.bind(this);
        this.refreshUtapiData = this.refreshUtapiData.bind(this);
        this.mergeUtapiDataInState = this.mergeUtapiDataInState.bind(this);
        this.mounted = false;
        this.metrics = new MetricsAPI();
    }

    getAccountRow(account) {
        const actGroup =
        <div className="btn-group btn-group-xs account-actions">
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="top"
            overlay={
              <Tooltip id="outgoingDataTooltip">
                Account metrics
              </Tooltip>
            }>
              <div
                className="btn btn-primary btn-xs"
                style={brandingStyles.buttons.primary}
              >
                <Link
                  to={`/accounts/metrics/${account.name}/${account.id}/` +
                  `${account.quota || 0}`}
                  style={{ color: 'white' }}
                >
                <i className="fa fa-pie-chart"/>
                </Link>
              </div>
          </OverlayTrigger>
          <ListKeysButton
            username={account.name}
            userType="account"
            getKeys={AccountsAPI.getKeysForAccount}
            deleteKey={AccountsAPI.deleteKeyForAccount}
            createKey={AccountsAPI.generateKeysForAccount}
            callback={this.handleUserAction}
          />
          <SetPasswordAccountButton
            username={account.name}
            callback={this.handleUserAction}
          />
          <SetAccountQuotaButton
            username={account.name}
            callback={this.handleUserAction}
          />
          <DeleteAccountButton
            username={account.name}
            callback={this.handleUserAction}
          />
        </div>;

        const arnPopover = (
            <Popover
              id="popover-account-arn-value"
              title="Amazon Resource Name (ARN)"
            >
              <p style={{ wordWrap: 'break-word' }}>
                  {account.arn}
              </p>
              <hr />
              <strong>
                {this.state.copiedState
                  ? 'Copied' : 'Click to copy to clipboard'}
              </strong>
            </Popover>
        );

        const arnEyeIcon = (
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="top"
                overlay={arnPopover}>
                    <i
                      className="fa fa-eye"
                      style={Object.assign(
                          {
                              fontSize: '18px',
                              cursor: 'pointer',
                          },
                        brandingStyles.mainColor
                      )}
                      onClick={this.copyArnToClipboard.bind(this, account.arn)}
                    />
            </OverlayTrigger>
        );

        const getQuotaColumn = quota => {
            if (quota === 0) {
                return '-';
            }
            return metricsTools.bytesToSize(quota);
        };

        return (
            <Tr
              key={account.id}
              className="account-row"
              style={brandingStyles.fontColor}
            >
                <Td
                  column="name"
                  data={account.name}
                  className="padding-l-25"
                  style={Object.assign(
                      {
                          fontWeight: 'bold',
                          fontSize: '16px',
                      },
                      brandingStyles.fontColor
                )}>
                    {account.name}
                </Td>
                <Td column="arn" style={{
                    fontStyle: 'italic',
                }}>
                    {arnEyeIcon}
                </Td>
                <Td column="storageUtilized" value={account.storageUtilized}>
                  {
                      !this.state.metricsError &&
                    (account.storageUtilized !== undefined ?
                    metricsTools.bytesToSize(account.storageUtilized) :
                    <i className="fa fa-refresh fa-spin"/>)
                    || '-'
                  }
                </Td>
                <Td column="quota" value={account.quota}>
                  {
                      (!this.state.metricsError ?
                    getQuotaColumn(account.quota || 0) :
                    <i className="fa fa-refresh fa-spin"/>)
                  }
                </Td>
                <Td column="UsedBandwidth">
                  {
                      !this.state.metricsError &&
                    (account.incomingBytes !== undefined ?
                    <span>
                      <OverlayTrigger
                        trigger={['hover', 'focus']}
                        placement="top"
                        overlay={
                          <Tooltip id="incomingDataTooltip">
                            Incoming data
                          </Tooltip>
                        }>
                          <span>
                              <i
                                style={brandingStyles.mainColor}
                                className="fa fa-arrow-circle-up"
                              />
                              &nbsp;
                              {metricsTools.bytesToSize(account.incomingBytes)}
                          </span>
                      </OverlayTrigger>
                      &nbsp;
                      <OverlayTrigger
                        trigger={['hover', 'focus']}
                        placement="top"
                        overlay={
                          <Tooltip id="outgoingDataTooltip">
                            Outgoing data
                          </Tooltip>
                        }>
                          <span>
                              <i
                                style={brandingStyles.mainColor}
                                className="fa fa-arrow-circle-down"
                              />
                              &nbsp;
                              {metricsTools.bytesToSize(account.outgoingBytes)}
                          </span>
                      </OverlayTrigger>
                    </span> :
                    <i className="fa fa-refresh fa-spin"/>)
                    || '-'
                  }
                </Td>
                <Td column="createDate">
                    {getDateFormat(new Date(account.createDate))}
                </Td>
                <Td column="inDatabase">
                    <i className={
                        account.inDatabase ? 'fa fa-check' : 'fa fa-times'
                      } />
                </Td>
                <Td column="Actions">
                  {actGroup}
                </Td>
            </Tr>
        );
    }

    componentDidMount() {
        this.mounted = true;
        this.refresh();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    // To handle table filter change
    handleFilterChange(e) {
        if (this.mounted) {
            this.setState({ filterText: e.target.value });
        }
    }

    handlePreviousPage() {
        if (this.mounted) {
            this.setState({
                currentIndex: this.state.currentIndex - 1,
            });
        }
    }

    handleNextPage() {
        const currentIndex = this.state.currentIndex;
        if (this.mounted) {
            this.setState({
                currentIndex: this.state.currentIndex + 1,
            });
        }
        if (currentIndex + 1 === this.state.accounts.length) {
            this.refresh();
        }
    }

    handleUserAction() {
        if (this.mounted) {
            this.setState({
                accounts: [],
                filterText: '',
                currentIndex: 0,
                maxIndex: -1,
                marker: null,
            }, this.refresh);
        }
    }

    handleNbItemsSelect(event) {
        localStorage.setItem('scality-nbitems-vault-accounts',
        JSON.stringify(event.target.value));
        if (this.mounted) {
            this.setState({
                nbItems: event.target.value,
            }, this.handleUserAction);
        }
    }

    copyArnToClipboard(value) {
        const success = copyToClipboard(value);
        this.setCopied(success);
    }

    setCopied(success) {
        if (success && this.mounted) {
            this.setState({ copiedState: true });
            setTimeout(() => {
                this.setState({ copiedState: false });
            }, 2000);
        }
    }

    mergeUtapiDataInState(data, index) {
        // Deep cloning existing accounts array
        const current = JSON.parse(JSON.stringify(this.state.accounts[index]));

        if (current.length === data.length) {
            for (let i = 0; i < current.length; i++) {
                for (let j = 0; j < data.length; j++) {
                    if (data[j].accountId === current[i].id) {
                        current[i].storageUtilized = data[j].storageUtilized;
                        current[i].incomingBytes = data[j].incomingBytes;
                        current[i].outgoingBytes = data[j].outgoingBytes;
                    }
                }
            }
        }
        const res = this.state.accounts;
        res[index] = current;
        if (this.mounted) {
            this.setState({ accounts: res });
        }
    }

    refreshUtapiData() {
        const startDate = moment().subtract(6, 'days');
        const endDate = moment();
        /*
        ** Store this in a variable to make sure
        ** it's consistent when API answers
        */
        const fixedCurrentIndex = this.state.currentIndex;

        const idArray =
        this.state.accounts[fixedCurrentIndex].map(item => ({ accountId: item.id, accountName: item.name }));
        if (idArray.length === 0) {
            return undefined;
        }
        return this.metrics.getMultipleAccountsMetrics(
          idArray,
          metricsTools.momentToStart(startDate),
          metricsTools.momentToEnd(endDate)
        )
        .then(res => {
            this.mergeUtapiDataInState(res.metrics, fixedCurrentIndex);
        })
        .catch(() => {
            if (this.mounted) {
                this.setState({
                    metricsError: true,
                });
            }
        });
    }


    // Refresh accounts list
    refresh() {
        AccountsAPI.getAccounts(this.state.nbItems, this.state.marker)
        .then(res => {
            const accountsPage = this.state.accounts;
            accountsPage.push(res.accounts);
            if (this.mounted) {
                this.setState({
                    accounts: accountsPage,
                    marker: res.marker,
                    maxIndex: res.marker ? -1 : this.state.currentIndex,
                    dataLoaded: true,
                }, () => {
                    this.refreshUtapiData();
                });
            }
            // Default sorting for table
            this.sortTable({ column: 'name', direction: 1 });
        }).catch(message => {
            if (message.status === 401 || message.status === 403) {
                if (message.response.body.error === 'The AWS access key Id ' +
                    'you provided does not exist in our records.') {
                    window.location.href =
                        `${namespace}/login/error/invalidkey`;
                } else {
                    window.location.href =
                        `${namespace}/login/error/${message.status}`;
                }
            }
        }
        );
    }

    render() {
        let accountsList;
        const currentAccounts = this.state.accounts[this.state.currentIndex];
        if (currentAccounts) {
            // Map over the accounts and get an element for each of them
            accountsList = currentAccounts.map(
              (user, index) => this.getAccountRow(user, index)
            );
        }

        const passwordColumnTooltip = (
          <Tooltip id="tooltip">
            If account has a password set to log in the IAM UI
          </Tooltip>
        );

        const arnPopover = (
            <Popover
              id="popover-account-arn"
              title="Amazon Resource Name (ARN)"
            >
                <p>
                    {'Each IAM account has a unique ARN with the format: '}
                </p>
                <p>
                    {'arn:<partition>:<service>::<account-id>:'}
                    {'<resourcetype/resource>'}
                </p>
            </Popover>
        );

        const arnHelpIcon = (
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="right"
                overlay={arnPopover}>
                    <i className="fa fa-question-circle-o"></i>
            </OverlayTrigger>
        );

        const quotaPopover = (
            <Popover
              id="popover-account-quota"
              title="Account quota"
            >
                <p>
                    {'The quota is a disk usage limit applied to an account.'}
                </p>
                <p>
                    {'A quota of "-" means that the account does not have '}
                    {'a usage limit.'}
                </p>
            </Popover>
        );

        const quotaHelpIcon = (
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="right"
                overlay={quotaPopover}>
                    <i className="fa fa-question-circle-o"></i>
            </OverlayTrigger>
        );


        const table = <Table className="table table-striped" id="table"
          sortable={[
              'name',
              'storageUtilized',
              {
                  column: 'createDate',
                  sortFunction: compareDates,
              },
          ]}
          filterBy={this.state.filterText}
          hideFilterInput
          filterable={['name', 'arn', 'createDate']}>
            <Thead>
                <Th column="name" className="padding-l-25">
                    <strong className="name-header">Account name</strong>
                </Th>
                <Th column="arn">
                    <strong className="name-header">ARN</strong>
                    {' '}{arnHelpIcon}
                </Th>
                <Th column="storageUtilized">
                    <strong className="name-header">Used Storage</strong>
                </Th>
                <Th column="quota">
                    <strong className="name-header">Quota</strong>
                    {' '}{quotaHelpIcon}
                </Th>
                <Th column="UsedBandwidth">
                    <strong className="name-header">Last week Bandwidth</strong>
                </Th>
                <Th column="createDate">
                    <strong className="name-header">Created on</strong>
                </Th>
                <Th column="inDatabase">
                    <strong className="name-header">
                      <OverlayTrigger
                        placement="top"
                        overlay={passwordColumnTooltip}
                      >
                        <i className="fa fa-lock"/>
                      </OverlayTrigger>
                    </strong>
                </Th>
                <Th column="Actions">
                    <strong className="name-header">Actions</strong>
                </Th>
            </Thead>
            {accountsList}
        </Table>;

        const prevDisabled = this.state.currentIndex === 0;
        const nextDisabled = this.state.currentIndex === this.state.maxIndex;

        const pagerInstance = (
          <ul className="pager padding-l-15 padding-r-15">
            <li
              className={prevDisabled ? 'previous disabled' : 'previous'}
            >
              <a
                className="clickable"
                onClick={this.handlePreviousPage}
                style={brandingStyles.buttons.primary}
              >&larr;</a>
            </li>
            <li
              className={nextDisabled ? 'next disabled' : 'next'}
            >
              <a
                className="clickable"
                onClick={this.handleNextPage}
                style={brandingStyles.buttons.primary}
              >&rarr;</a>
            </li>
          </ul>
        );

        return (
            <div id="page-wrapper" style={brandingStyles.backgrounds.main}>
                <PageHeader facode="users" title="Accounts"/>
                <div
                  className="panel panel-default"
                  style={brandingStyles.backgrounds.panel}
                >
                    <div className="panel-body padding-0">
                        <div className="padding-15">
                            <div style={{
                                display: 'inline-block',
                            }}>
                                <NewAccountButton
                                callback={this.handleUserAction}/>
                            </div>
                            <select
                            className="pull-right form-control
                            pagination-select"
                            style={brandingStyles.backgrounds.input}
                            value={this.state.nbItems}
                            onChange={this.handleNbItemsSelect.bind(this)}>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                        <input className="form-control
                          full-width accounts-finder"
                          placeholder="Find accounts"
                          style={brandingStyles.backgrounds.input}
                          value={this.state.filterText}
                          onChange={this.handleFilterChange}>
                        </input>
                        <div className="table-responsive">
                          {this.state.dataLoaded ? table : <LoadingSpinner />}
                        </div>
                        {pagerInstance}
                    </div>
                </div>
            </div>
        );
    }
}

export default AccountsComponent;



/** WEBPACK FOOTER **
 ** ./src/pages/Accounts.js
 **/